import React, { useMemo, useState, useEffect } from "react";
import { DayPost } from "../../types/post";
import { generateDateID, getDayPost } from "../../services/dayPostService";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeStore } from "../../stores/themeStore";

interface TrainingDaysProps {
  dayPosts: DayPost[];
  className?: string;
  targetLoad?: number; // default 400
  loadBalance?: number; // Today's workloadFactorDisplay (falls back to workloadFactor) from peakScoreLog
  acuteLoad?: number; // EWMA_WorkA from peakScoreLog (acute training load)
}

const TrainingDays: React.FC<TrainingDaysProps> = ({
  dayPosts,
  className = "",
  targetLoad = 400,
  loadBalance,
  acuteLoad,
}) => {
  const { currentUser } = useAuth();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";

  // State for 28-day data fetching
  const [extended28DayData, setExtended28DayData] = useState<DayPost[]>([]);
  const [loading28DayData, setLoading28DayData] = useState(false);
  const [has28DayDataLoaded, setHas28DayDataLoaded] = useState(false);

  // Effect to fetch 28-day data when component mounts
  useEffect(() => {
    const fetch28DayData = async () => {
      if (!currentUser?.uid || loading28DayData || has28DayDataLoaded) return;

      setLoading28DayData(true);

      try {
        const today = new Date();
        const dateIds: string[] = [];

        // Generate last 28 days worth of dateIDs
        for (let i = 27; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          dateIds.push(generateDateID(date));
        }

        // Fetch data for dates that aren't already in dayPosts
        const existingDateIds = new Set(dayPosts.map((post) => post.dateID));
        const missingDateIds = dateIds.filter(
          (dateId) => !existingDateIds.has(dateId),
        );

        // console.log(
        //   `[TrainingDays-28Day] Fetching ${missingDateIds.length} missing days for 28-day comparison`,
        // );

        // Fetch missing days
        const missingDayPosts = await Promise.all(
          missingDateIds.map(async (dateId) => {
            try {
              const dayPost = await getDayPost(currentUser.uid, dateId);
              return dayPost;
            } catch (error) {
              // console.warn(
              //   `[TrainingDays-28Day] Failed to fetch ${dateId}:`,
              //   error,
              // );
              return null;
            }
          }),
        );

        // Filter out nulls and combine with existing data
        const validMissingPosts = missingDayPosts.filter(
          (post): post is DayPost => post !== null,
        );
        const combinedData = [...dayPosts, ...validMissingPosts];

        setExtended28DayData(combinedData);
        setHas28DayDataLoaded(true);

        // console.log(
        //   `[TrainingDays-28Day] Successfully loaded ${validMissingPosts.length} additional days`,
        // );
      } catch (error) {
        // console.error(
        //   "[TrainingDays-28Day] Error fetching 28-day data:",
        //   error,
        // );
        // Fallback to existing data
        setExtended28DayData(dayPosts);
        setHas28DayDataLoaded(true);
      } finally {
        setLoading28DayData(false);
      }
    };

    fetch28DayData();
  }, [currentUser?.uid, dayPosts, loading28DayData, has28DayDataLoaded]);

  const {
    weekData,
    last28DaysTotal,
    percentageDifference,
    actualDaysAvailable,
  } = useMemo(() => {
    const today = new Date();
    const days: Array<{
      dateID: string;
      dayName: string;
      totalLoad: number;
      isToday: boolean;
    }> = [];

    // Calculate last 7 days data
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      const dateID = generateDateID(date);
      const dayName = date
        .toLocaleDateString("en-US", { weekday: "narrow" })
        .toUpperCase();

      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      const totalLoad = (dayPost?.trainingLogs || []).reduce((sum, log) => {
        // Debug: Log training data to verify load values
        if (log.load && log.load > 0) {
          // console.log(
          //   `[TrainingDays-7Day] ${dateID}: Load=${log.load}, RPE=${log.rpe}, Duration=${log.duration}`,
          // );
        }
        return sum + (log.load || 0);
      }, 0);

      // console.log(
      //   `[TrainingDays-7Day] ${dateID}: Total daily load = ${totalLoad}`,
      // );

      days.push({
        dateID,
        dayName,
        totalLoad,
        isToday: dateID === generateDateID(today),
      });
    }

    // Calculate 28-day comparison load using extended data when available
    const dataSourceForComparison = has28DayDataLoaded
      ? extended28DayData
      : dayPosts;
    let comparisonPeriodTotal = 0;
    let actualDaysFound = 0;

    // Always check 28 days when we have extended data, otherwise use available data
    const daysToCheck = has28DayDataLoaded
      ? 28
      : Math.min(28, dayPosts.length * 4);

    for (let i = daysToCheck - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateID = generateDateID(date);

      const dayPost = dataSourceForComparison.find(
        (post) => post.dateID === dateID,
      );
      if (dayPost) {
        actualDaysFound++;
        const totalLoad = (dayPost.trainingLogs || []).reduce(
          (sum, log) => sum + (log.load || 0),
          0,
        );
        comparisonPeriodTotal += totalLoad;
      }
    }

    const last28DaysTotal = comparisonPeriodTotal;

    const last7DaysTotal = days.reduce((sum, day) => sum + day.totalLoad, 0);

    // Use actual days found for average calculation to avoid skewing results
    const comparisonPeriodAverage =
      actualDaysFound > 0 ? comparisonPeriodTotal / actualDaysFound : 0;
    const last7DaysAverage = last7DaysTotal / 7;

    // Calculate percentage difference with proper bounds handling
    // Only show meaningful percentages when there's sufficient training data
    const percentDiff = (() => {
      // If no training in either period, show 0%
      if (comparisonPeriodAverage === 0 && last7DaysAverage === 0) return 0;

      // If we don't have enough comparison data, show a simple indicator
      if (actualDaysFound < 7) {
        if (last7DaysAverage > comparisonPeriodAverage) return 100;
        if (last7DaysAverage < comparisonPeriodAverage) return -100;
        return 0;
      }

      // If baseline is too small (less than 5 per day average), don't show percentage
      // as it's not statistically meaningful
      const minThreshold = 5;
      if (comparisonPeriodAverage < minThreshold) {
        if (last7DaysAverage > comparisonPeriodAverage) return 100;
        if (last7DaysAverage < comparisonPeriodAverage) return -100;
        return 0;
      }

      // If no recent training but baseline exists, show as negative change
      if (last7DaysAverage === 0) return -100;

      // Normal calculation with cap at ±200% to avoid extreme values
      const rawPercent =
        ((last7DaysAverage - comparisonPeriodAverage) /
          comparisonPeriodAverage) *
        100;
      return Math.max(-200, Math.min(200, rawPercent));
    })();

    // Debug: Log the calculation values
    // console.log(`[TrainingDays] Calculation Summary:`);
    // console.log(`  Available dayPosts: ${dayPosts.length}`);
    // console.log(`  Extended 28-day data loaded: ${has28DayDataLoaded}`);
    // console.log(`  Extended data count: ${extended28DayData.length}`);
    // console.log(
    //   `  Data source: ${
    //     has28DayDataLoaded ? "Extended 28-day" : "Original dayPosts"
    //   }`,
    // );
    // console.log(`  Days found in comparison period: ${actualDaysFound}`);
    // console.log(`  7-day total: ${last7DaysTotal}`);
    // console.log(`  Comparison period total: ${last28DaysTotal}`);
    // console.log(`  7-day average: ${last7DaysAverage.toFixed(1)}`);
    // console.log(
    //   `  Comparison period average: ${comparisonPeriodAverage.toFixed(1)}`,
    // );
    // Show what the raw percentage would be without caps
    const rawUncappedPercent =
      comparisonPeriodAverage > 0
        ? ((last7DaysAverage - comparisonPeriodAverage) /
            comparisonPeriodAverage) *
          100
        : 0;
    // console.log(`  Raw uncapped percentage: ${rawUncappedPercent.toFixed(1)}%`);
    // console.log(`  Final capped percentage: ${percentDiff.toFixed(1)}%`);

    return {
      weekData: days,
      last28DaysTotal: last28DaysTotal,
      percentageDifference: percentDiff,
      actualDaysAvailable: actualDaysFound,
    };
  }, [dayPosts, has28DayDataLoaded, extended28DayData]);

  const totalWeekLoad = weekData.reduce((sum, day) => sum + day.totalLoad, 0);
  const averageDailyLoad = totalWeekLoad / 7;

  return (
    <div className={`bg-base-100 rounded-lg ${className}`}>
      <div className="flex items-center justify-between">
        {/* Left summary */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-grotesk">
              Training{" "}
              <span className="font-grotesk text-base-content/60">Load</span>
            </h3>
          </div>
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-5xl font-bold text-base-content font-heading">
              {Math.round(
                acuteLoad !== undefined ? acuteLoad : averageDailyLoad,
              )}
            </span>

            {/* Load Balance indicator (100 - loadBalance) */}
            {loadBalance !== undefined && (
              <span className="text-sm font-medium px-2 py-1 rounded-md bg-base-200 text-base-content/80">
                {(() => {
                  const diff = Math.round(100 - loadBalance * 100);
                  return `${diff > 0 ? "+" : ""}${diff}%`;
                })()}
              </span>
            )}
          </div>
          <p className="text-xs text-base-content/60">
            {acuteLoad !== undefined
              ? "Acute Training Load"
              : "Last 7 Days average"}
          </p>
        </div>

        {/* Right visualization */}
        <div className="flex flex-col items-center mt-2">
          <div className="flex space-x-1 items-end">
            {weekData.map((day) => {
              const ratio = Math.min(day.totalLoad / targetLoad, 1);
              const heightPct = Math.round(ratio * 100);
              const hitTarget = day.totalLoad >= targetLoad;

              return (
                <div key={day.dateID} className="w-4 h-12 relative">
                  <div
                    className={`absolute inset-0 rounded-full ${isDark ? "bg-gray-900" : "bg-gray-200"}`}
                  />
                  <div
                    className={`absolute bottom-0 left-0 right-0 rounded-full ${
                      hitTarget
                        ? isDark
                          ? "bg-gray-400"
                          : "bg-gray-700"
                        : isDark
                          ? "bg-gray-700"
                          : "bg-gray-400"
                    }`}
                    style={{ height: hitTarget ? "100%" : `${heightPct}%` }}
                  />
                  {hitTarget && (
                    <div
                      className={`absolute inset-0 flex items-center justify-center ${isDark ? "text-gray-800" : "text-white"}`}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Day labels */}
          <div className="flex space-x-1">
            {weekData.map((day) => (
              <div key={`${day.dateID}-label`} className="w-4 text-center">
                <span
                  className={`text-xs font-medium ${
                    day.isToday
                      ? "text-base-content font-semibold"
                      : "text-base-content/60"
                  }`}
                >
                  {day.dayName}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingDays;
